import { FC } from 'react';
import { Link, navigate } from 'gatsby';
import { ButtonOutlinePrimary } from '@lambdacurry/component-library';
import { useAddReturningDiscountCodeToCartMutation, useCancelSubscriptionMutation } from 'lib/home/generated/queries';
import { CancellationReasons, OrdersQuery, SubscriptionStatus } from 'lib/home/generated/types/api';
import { Card, CardHeader, CardSection, Grid } from 'lib/components';
import { CancelPlanReasonFormValues, DefaultLayout, RedirectRouter } from '../../../components';
import { useQueryClient } from 'react-query';

export interface CancellationStepProps {
  cancellation: CancelPlanReasonFormValues;
}

export interface CancellationStepPageProps {
  location?: { state: { cancellation: CancelPlanReasonFormValues } };
}

const CancelSuggestions: FC<CancellationStepProps> = ({ cancellation }) => {
  const reason = cancellation?.reason;
  const queryClient = useQueryClient();
  const { mutate: cancelSubscription } = useCancelSubscriptionMutation();
  const { mutate: addReturningDiscountCodeToCart } = useAddReturningDiscountCodeToCartMutation();

  const handleCancel = () =>
    cancelSubscription(
      {},
      {
        onSuccess: ({ cancelSubscription }) => {
          window.dataLayer.push({
            event: 'cancel_subscription',
            user_id: cancelSubscription.user_id,
            subscription_id: cancelSubscription.id
          });

          queryClient.setQueryData(['Orders'], (query: OrdersQuery) => {
            query.me.subscription.status = SubscriptionStatus.Cancelled;
            return query;
          });

          navigate('/account/cancel/confirm', { state: { cancellation } });
        }
      }
    );

  const handleDiscount = () => addReturningDiscountCodeToCart({}, { onSuccess: () => navigate('/') });

  return (
    <DefaultLayout backTo="/account/cancel/feedback">
      <Grid>
        <Card className="col-span-12">
          <CardSection>
            <CardHeader>
              {reason === CancellationReasons.TooExpensive && (
                <>
                  <h3>Receive 35% off your next order</h3>
                  <p>We can give you a deal for your next week if you would like to stick around a little longer</p>
                  <ButtonOutlinePrimary className="mt-2 w-full md:w-56" onClick={handleDiscount}>
                    Okay, I'll save money
                  </ButtonOutlinePrimary>
                </>
              )}
              {reason === CancellationReasons.TooMany && (
                <>
                  <h3>Getting too much food?</h3>
                  <p>You can change your frequency to biweekly, or even every third or fourth week.</p>
                  <ButtonOutlinePrimary
                    className="mt-2 w-full md:w-56"
                    as={buttonProps => <Link {...buttonProps} to="/account/change-frequency" />}
                  >
                    View Frequency Options
                  </ButtonOutlinePrimary>
                </>
              )}
            </CardHeader>
          </CardSection>
        </Card>

        <Card className="col-span-12">
          <CardSection>
            <CardHeader>
              <h3>No thanks, I would still like to cancel</h3>
            </CardHeader>

            <ButtonOutlinePrimary className="mt-2 w-full md:w-56" onClick={handleCancel}>
              Cancel Subscription
            </ButtonOutlinePrimary>
          </CardSection>
        </Card>
      </Grid>
    </DefaultLayout>
  );
};

const CancelSuggestionsPage: FC<CancellationStepPageProps> = ({ location }) => (
  <RedirectRouter
    component={<CancelSuggestions cancellation={location?.state?.cancellation} />}
    location={location}
    to="/account/cancel"
  />
);

export default CancelSuggestionsPage;
